import { useState } from "react";
import { Layers, Loader2, Check, Star } from "lucide-react";
import { Button } from "./ui/button";

const STYLES = [
  { id: "classic", label: "Classic", blurb: "Clean, friendly duck face" },
  { id: "detailed", label: "Detailed", blurb: "Full likeness of your features" },
  { id: "hybrid", label: "Hybrid", blurb: "Classic look + your signature traits" },
];

// Step-2 "Compare Styles": renders classic/detailed/hybrid side by side (cached thumbs shown instantly on reopen);
// picking one makes it THE render to sculpt. `recommended` pre-highlights the best-fit style.
export const StylePicker = ({ creation, onGenerate, onSelect, disabled, recommended }) => {
  const [styles, setStyles] = useState(creation.style_thumbs || null); // { classic, detailed, hybrid } data-urls
  const [loading, setLoading] = useState(false);
  const [selecting, setSelecting] = useState(null);
  const chosen = creation.face_style;

  const generate = async () => {
    setLoading(true);
    try {
      const res = await onGenerate();
      if (res) setStyles(res);
    } finally {
      setLoading(false);
    }
  };

  const pick = async (id) => {
    setSelecting(id);
    try {
      await onSelect(id);
    } finally {
      setSelecting(null);
    }
  };

  if (!styles)
    return (
      <div>
        <Button data-testid="compare-styles-btn" variant="default" className="rounded-full gap-2 h-11 w-full sm:w-auto font-bold" onClick={generate} disabled={disabled || loading}>
          {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Layers className="h-4 w-4" />}
          {loading ? "Rendering styles…" : "Compare Styles"}
        </Button>
        <p className="text-xs text-muted-foreground mt-2">
          See Classic, Detailed & Hybrid side by side, then pick the one to sculpt.
          {recommended && <> We suggest <span className="font-semibold capitalize">{recommended}</span> for this photo.</>}
        </p>
      </div>
    );

  return (
    <div data-testid="style-picker">
      <p className="text-xs font-mono2 uppercase tracking-widest text-muted-foreground mb-3 flex items-center gap-2">
        <Layers className="h-3.5 w-3.5 text-accent" /> Pick a style to sculpt
      </p>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        {STYLES.map((s) => {
          const src = styles[s.id];
          const isChosen = chosen === s.id;
          const isRec = recommended === s.id;
          const highlight = isChosen || (!chosen && isRec);
          return (
            <div key={s.id} data-testid={`style-card-${s.id}`} className={`relative rounded-2xl border p-3 flex flex-col ${highlight ? "border-accent ring-2 ring-accent/40" : "border-border"}`}>
              {isRec && (
                <span data-testid={`style-recommended-${s.id}`} className="absolute -top-2 left-3 z-10 rounded-full bg-accent text-accent-foreground text-[10px] font-bold uppercase tracking-wide px-2 py-0.5 inline-flex items-center gap-1">
                  <Star className="h-3 w-3 fill-current" /> Recommended
                </span>
              )}
              <div className="rounded-xl overflow-hidden bg-muted aspect-square mb-3">
                {src ? <img src={src} alt={s.label} className="w-full h-full object-cover" /> : <div className="w-full h-full grid place-items-center text-xs text-muted-foreground">unavailable</div>}
              </div>
              <p className="font-heading font-bold">{s.label}</p>
              <p className="text-xs text-muted-foreground mb-3 flex-1">{s.blurb}</p>
              <Button data-testid={`style-select-${s.id}`} variant={isChosen ? "default" : "secondary"} className="rounded-full gap-2 h-10 w-full" onClick={() => pick(s.id)} disabled={disabled || !src || !!selecting}>
                {selecting === s.id ? <Loader2 className="h-4 w-4 animate-spin" /> : isChosen ? <Check className="h-4 w-4" /> : null}
                {isChosen ? "Selected" : "Use this style"}
              </Button>
            </div>
          );
        })}
      </div>
      <Button data-testid="restyle-btn" variant="ghost" className="rounded-full mt-3 text-sm" onClick={generate} disabled={disabled || loading}>
        {loading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
        Re-render styles with current features
      </Button>
    </div>
  );
};
